import { ConfigurationTablePageSortSettings } from 'src/shared/constants/table-settings-constants'
import { useCallback, useEffect, useMemo } from 'react'
import { STORAGE_KEY } from 'src/shared/utils/StorageUtils'
import { useLocalStorage } from 'src/shared/utils/hooks/local-storage-hooks'
import { Option } from 'src/shared/form/control'

const CONFIG_SELECT_FILTER_KEYS = {
  FEATURES_KAP_FACTSHEET: ['featuresKapFactsheetLevelFilter'] as const,
  FEATURE_TYPE: ['featureTypesProcessFilter', 'featureTypesStatusFilter'] as const,
  FEATURE_GROUP: ['featureGroupsTypeFilter', 'featureGroupsStatusFilter'] as const,
  FEATURE_CONFIG: ['featuresProcessFilter', 'featuresTypeFilter', 'featuresStatusFilter'] as const,
  CRITERIA_PF_KAP: ['criteriaPfKapApplicationTypeFilter', 'criteriaPfKapCriteriaTypeFilter'] as const,
  CRITERIA_PF_PGV: ['criteriaPfPgvApplicationTypeFilter', 'criteriaPfPgvCriteriaTypeFilter'] as const,
  CRITERIA_FACTSHEET: ['criteriaFactsheetCriteriaTypeFilter', 'criteriaFactsheetLevelFilter'] as const,
  EVALUATION_CRITERIA: [
    'criteriaProcessFilter',
    'criteriaApplicationTypeFilter',
    'criteriaTypeFilter',
    'criteriaStatusFilter',
  ] as const,
  CRITERIA_GROUP: ['criteriaGroupProcessFilter', 'criteriaGroupStatusFilter'] as const,
  MILESTONE_CONFIG: ['milestoneProcessFilter', 'milestoneTypeFilter', 'milestoneResponsibleFilter'] as const,
} as const

const CONFIG_STRING_FILTER_KEYS = {
  HELP_INSTRUCTION: ['helpInstructionSearchFilter', 'helpInstructionSearchFilterTyped'] as const,
}

const ALL_FILTER_KEYS = {
  ...CONFIG_SELECT_FILTER_KEYS,
  ...CONFIG_STRING_FILTER_KEYS,
}

export type ConfigTableFilters = {
  [K in (typeof CONFIG_SELECT_FILTER_KEYS)[keyof typeof CONFIG_SELECT_FILTER_KEYS][number]]: Option
} & {
  [K in (typeof CONFIG_STRING_FILTER_KEYS)[keyof typeof CONFIG_STRING_FILTER_KEYS][number]]: string
}

export type SpecificConfigFilter<K extends keyof typeof ALL_FILTER_KEYS> = Pick<
  ConfigTableFilters,
  (typeof ALL_FILTER_KEYS)[K][number]
>

export type CONFIG_TABLE_NAME =
  | 'FEATURE_GROUP'
  | 'FEATURE_TYPE'
  | 'FEATURE_CONFIG'
  | 'EVALUATION_CRITERIA'
  | 'CRITERIA_GROUP'
  | 'MILESTONE_CONFIG'
  | 'HELP_INSTRUCTION_CONFIG'

type ConfigTablePaginationsAndSortings = {
  [K in CONFIG_TABLE_NAME]: ConfigurationTablePageSortSettings
}

/**
 * Checks if the provided object contains all the specified keys.
 *
 * This utility is intended for use when retrieving objects from local storage,
 * to verify that the stored object includes all the necessary keys required by
 * the filters of the current configuration grid that is being displayed.
 *
 * @param obj - The object fetched from local storage to validate.
 * @param keys - An array of keys that must be present in the object.
 * @returns True if the object contains all specified keys; otherwise, false.
 */
const storedObjectHasKeys = <K extends readonly string[]>(
  obj: unknown,
  keys: K,
): obj is { [P in K[number]]: unknown } => {
  return !!obj && typeof obj === 'object' && keys.every((key) => key in obj)
}

/**
 * Hook to manage the pagination and sorting models of configuration grids and sync them with local storage.
 *
 * Different configuration grid table settings use the same STORAGE_KEY in local storage.
 *
 * This useLocalStorage wrapper manages the storage of the grid pagination and sorting models of the specific
 * configuration that calls this hook.
 *
 * - effectiveSettings: current table settings (pagination and sorting models)
 * - setStoredSettings: setter to update the table settings
 */
export const useTableSettingsFromLocalStorage = (
  configTableName: CONFIG_TABLE_NAME,
  defaultSettings: ConfigurationTablePageSortSettings,
): [ConfigurationTablePageSortSettings, React.Dispatch<ConfigurationTablePageSortSettings>] => {
  const [storedSettings, setStoredSettings] = useLocalStorage<Partial<ConfigTablePaginationsAndSortings>>(
    STORAGE_KEY._CONFIG_GRID_TABLE_SETTINGS_STORAGE_V2,
  )

  const effectiveSettings: ConfigurationTablePageSortSettings = useMemo(() => {
    if (storedObjectHasKeys(storedSettings, [configTableName])) {
      return storedSettings[configTableName] ?? defaultSettings
    }

    return defaultSettings
  }, [configTableName, defaultSettings, storedSettings])

  const setEffectiveSettings = useCallback(
    (value: ConfigurationTablePageSortSettings) => {
      setStoredSettings({
        ...storedSettings,
        [configTableName]: value,
      })
    },
    [configTableName, setStoredSettings, storedSettings],
  )

  useEffect(() => {
    if (!storedObjectHasKeys(storedSettings, [configTableName])) {
      setEffectiveSettings(defaultSettings)
    }
  }, [configTableName, defaultSettings, setEffectiveSettings, storedSettings])

  return [effectiveSettings, setEffectiveSettings]
}

/**
 * Hook to manage the filter settings of a configuration grid and sync them with local storage.
 *
 * Different configuration grid filter settings use the same STORAGE_KEY in local storage.
 *
 * This useLocalStorage wrapper manages the storage of the grid filters of the specific configuration that calls this
 * hook.
 *
 * - effectiveFilters: current config filter settings
 * - setStoredFilters: setter to update the filter settings of the configuration grid
 */
export const useFilterSettingsFromLocalStorage = <FilterType extends Partial<ConfigTableFilters>>(
  defaultFilters: FilterType,
): [FilterType, React.Dispatch<FilterType>] => {
  const [storedFilters, setStoredFilters] = useLocalStorage<Partial<ConfigTableFilters>>(
    STORAGE_KEY._CONFIG_GRID_FILTER_STORAGE_V2,
  )

  const filterKeys = useMemo(
    () => Object.keys(defaultFilters) as Array<keyof Partial<ConfigTableFilters>>,
    [defaultFilters],
  )

  const effectiveFilters: FilterType = useMemo(() => {
    if (storedObjectHasKeys(storedFilters, filterKeys)) {
      return Object.fromEntries(filterKeys.map((key) => [key, storedFilters[key]])) as FilterType
    }

    return defaultFilters
  }, [storedFilters, filterKeys, defaultFilters])

  const setEffectiveFilters = useCallback(
    (value: FilterType) => {
      const updatedFilters = {
        ...storedFilters,
        ...value,
      }

      setStoredFilters(updatedFilters)
    },
    [storedFilters, setStoredFilters],
  )

  useEffect(() => {
    if (!storedObjectHasKeys(storedFilters, filterKeys)) {
      setEffectiveFilters(defaultFilters)
    }
  }, [defaultFilters, filterKeys, setEffectiveFilters, storedFilters])

  return [effectiveFilters, setEffectiveFilters]
}
